import type { FormEvent } from 'react';
import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useHomepagePageUserData } from 'src/app/pages/homepage/pageContext';
import { createDraft, type HomepageDraftInput, type HomepageDraftType } from 'src/app/pages/homepage/shared/api';

type SapPromptHookResult = ModelHookResult<
	{
		isSubmitting: boolean;
	},
	{
		onSubmit: (event: FormEvent<HTMLFormElement>) => void;
	}
>;

type ExperimentsData = ReturnType<typeof useHomepagePageUserData>['data']['experiments'];

/**
 * Determines the draft type based on the starfishSap3RecommendedAngles experiment variant.
 * - 'variant': returns 'recommendations_draft' to enable the recommendations flow
 * - 'control' or not enrolled: returns 'ai_draft' for the default AI draft flow
 */
function getDraftTypeFromExperiment(experiments: ExperimentsData): HomepageDraftType {
	const { variation } = experiments.starfishSap3RecommendedAngles;
	return variation.includes('control') ? 'ai_draft' : 'recommendations_draft';
}

export function useSapIssueForm(): SapPromptHookResult {
	const track = useTracking();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {
		data: { experiments },
	} = useHomepagePageUserData();

	const draftType = getDraftTypeFromExperiment(experiments);

	const { mutate, ...mutationState } = useMutation({
		mutationFn: async (draftInput: string) => {
			const payload: HomepageDraftInput = { issuePrompt: draftInput, draftType };
			return createDraft(payload, { queryClient });
		},
		onSuccess: () => {
			navigate('/start-a-petition/personal-story?started_flow=false&source_location=homepage_primary_button');
		},
		onError: () => {
			void track('homepage_sap_draft_submission_error');
		},
	});

	const isSubmitting = mutationState.isPending || mutationState.isSuccess;

	const handleSubmit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const formData = new FormData(event.target as HTMLFormElement);
			const input = (formData.get('issue') as string) || '';
			const trimmed = input.trim();
			void track('homepage_SAP_click', {
				cta: 'primary',
				prompt: trimmed,
			});

			if (isSubmitting) return;

			if (!trimmed.length) {
				navigate('/start-a-petition/intro?source_location=homepage_primary_button');
				return;
			}

			mutate(trimmed);
		},
		[mutate, navigate, track, isSubmitting],
	);

	return {
		data: {
			isSubmitting,
		},
		actions: {
			onSubmit: handleSubmit,
		},
	};
}
